import React from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  CardMedia,
  Divider,
  alpha,
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  ChatBubble,
  Share,
  MoreVert,
  VolumeUp,
  VolumeOff,
} from '@mui/icons-material';
import { Community, Post } from '../../types/community';

interface PostCardProps {
  post: Post;
  index: number;
  posts: Post[];
  community: Community;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
  handleVote: (postId: string, vote: 'up' | 'down' | 'remove') => void;
  handlePostMenuOpen: (event: React.MouseEvent<HTMLElement>, postId: string) => void;
  navigate: any;
  toggleVideoMute: (postId: string) => void;
  handleVideoRef: (postId: string, el: HTMLVideoElement | null) => void;
  videoMuted: {[key: string]: boolean};
  isMobile: boolean;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  index,
  posts,
  community,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
  handleVote,
  handlePostMenuOpen,
  navigate,
  toggleVideoMute,
  handleVideoRef,
  videoMuted,
  isMobile,
}) => {
  return (
    <React.Fragment key={post.id}>
      <Box
    onClick={() => navigate(`/post/${post.id}`)}
    sx={{
      cursor: 'pointer',
      // p: { xs: 2, md: 3 },
      // '&:hover': {
      //   bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
      // }
    }}
  >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
          <Avatar 
            src={post.author?.profilePicture} 
            sx={{ 
              width: { xs: 40, md: 52 }, 
              height: { xs: 40, md: 52 },
              border: `2px solid ${alpha(goldColor, 0.3)}`,
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}
          >
            {post.author?.username?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 800, 
                color: isDark ? '#fff' : deepGreen, 
                fontSize: { xs: '0.9rem', md: '1.1rem' }
              }}>
                {post.author?.username || 'Unknown'}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)', 
                fontSize: { xs: '0.7rem', md: '0.8rem' } 
              }}>
                posted in
              </Typography>
              <Typography variant="caption" sx={{ 
                color: goldColor,
                fontWeight: 800,
                fontSize: { xs: '0.75rem', md: '0.9rem' }
              }}>
                wfh/{community.slug}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="h5" sx={{ 
          fontWeight: 900, 
          mb: { xs: 2, md: 2.5 }, 
          lineHeight: 1.4, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' }
        }}>
          {post.title}
        </Typography>
         
        {(post.type === 'image' || post.type === 'video') && post.mediaUrl && (
          <Box sx={{ 
            mb: { xs: 2, md: 3 }, 
            borderRadius: '16px', 
            overflow: 'hidden', 
            position: 'relative',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` 
          }}>
            {post.type === 'image' ? (
              <CardMedia 
                component="img" 
                image={post.mediaUrl} 
                alt="Post" 
                sx={{ 
                  width: '100%', 
                  maxHeight: { xs: '300px', md: '500px' }, 
                  objectFit: 'cover',
                }} 
              />
            ) : (
              <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                <video
                  ref={(el) => handleVideoRef(post.id, el)}
                  src={post.mediaUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '16px'
                  }}
                  loop
                  autoPlay
                  playsInline
                  muted={videoMuted[post.id] !== false}
                />
                <IconButton
                  onClick={() => toggleVideoMute(post.id)}
                  sx={{
                    position: 'absolute',
                    bottom: { xs: 8, md: 16 },
                    right: { xs: 8, md: 16 },
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.8)'
                    },
                    width: { xs: 32, md: 40 },
                    height: { xs: 32, md: 40 }
                  }}
                >
                  {videoMuted[post.id] === false ? <VolumeUp /> : <VolumeOff />}
                </IconButton>
              </Box>
            )}
          </Box>
        )}
         
        {post.images && post.images.length > 0 && (
          <Box sx={{ 
            mb: { xs: 2, md: 3 }, 
            display: 'grid',
            gridTemplateColumns: post.images.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: { xs: 1, md: 2 },
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            {post.images.slice(0, 4).map((image: string, idx: number) => (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  paddingTop: post.images?.length === 1 ? '56.25%' : '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  cursor: 'pointer'
                }}
                onClick={() => window.open(image, '_blank')}
              >
                <CardMedia
                  component="img"
                  image={image}
                  alt={`Post image ${idx + 1}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {post.images && post.images.length > 4 && idx === 3 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(0,0,0,0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: { xs: '1rem', md: '1.5rem' },
                      fontWeight: 700
                    }}
                  >
                    +{post.images.length - 4}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {post.content && (
          <Typography variant="body1" sx={{ 
            mb: { xs: 2, md: 3 }, 
            color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
            lineHeight: 1.7,
            fontSize: { xs: '0.9rem', md: '1.05rem' },
            whiteSpace: 'pre-wrap',
            maxWidth: '700px'
          }}>
            {post.content.length > 300 ? `${post.content.substring(0, 300)}...` : post.content}
            {post.content.length > 300 && (
              <Button 
                size="small" 
                sx={{ 
                  color: goldColor, 
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  ml: 0.5,
                  minWidth: 'auto',
                  '&:hover': {
                    background: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
                onClick={() => navigate(`/post/${post.id}`)}
              >
                Read more
              </Button>
            )}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: { xs: 2, md: 3 } }}>
          {post.tags?.slice(0, 5).map((tag: string, idx: number) => (
            <Chip
              key={idx}
              label={`#${tag}`}
              size="small"
              sx={{
                bgcolor: alpha(goldColor, 0.1),
                color: goldColor,
                fontWeight: 600,
                borderRadius: '12px',
                fontSize: { xs: '0.65rem', md: '0.8rem' },
                height: { xs: 22, md: 32 },
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.2),
                }
              }}
            />
          ))}
          {post.tags && post.tags.length > 5 && (
            <Chip
              label={`+${post.tags.length - 5}`}
              size="small"
              sx={{
                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                fontWeight: 600,
                borderRadius: '12px',
                fontSize: { xs: '0.65rem', md: '0.8rem' },
                height: { xs: 22, md: 32 }
              }}
            />
          )}
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pt: 2,
          borderTop: `1px solid ${alpha(goldColor, 0.1)}`
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, md: 2 },
            flexWrap: 'wrap',
            width: '100%'
          }}>
            <Button
              startIcon={<ThumbUp sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
              onClick={() => handleVote(post.id, post.userVote === 'up' ? 'remove' : 'up')}
              sx={{
                minWidth: 'auto',
                color: post.userVote === 'up' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                fontWeight: 700,
                borderRadius: '20px',
                px: { xs: 1, md: 2 },
                py: { xs: 0.25, md: 0.5 },
                bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.1) : 'transparent',
                ...(post.userVote === 'up' ? {} : glassEffect),
                fontSize: { xs: '0.75rem', md: '0.9rem' },
                '&:hover': {
                  bgcolor: post.userVote === 'up' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05),
                }
              }}
            >
              {post.upvotes}
            </Button>
            
            <Button
              startIcon={<ThumbDown sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
              onClick={() => handleVote(post.id, post.userVote === 'down' ? 'remove' : 'down')}
              sx={{
                minWidth: 'auto',
                color: post.userVote === 'down' ? goldColor : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
                fontWeight: 700,
                borderRadius: '20px',
                px: { xs: 1, md: 2 },
                py: { xs: 0.25, md: 0.5 },
                bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.1) : 'transparent',
                ...(post.userVote === 'down' ? {} : glassEffect),
                fontSize: { xs: '0.75rem', md: '0.9rem' },
                '&:hover': {
                  bgcolor: post.userVote === 'down' ? alpha(goldColor, 0.2) : alpha(goldColor, 0.05),
                }
              }}
            >
              {post.downvotes}
            </Button>

            <Button
              startIcon={<ChatBubble sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
              onClick={() => navigate(`/post/${post.id}`)}
              sx={{
                minWidth: 'auto',
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                fontWeight: 700,
                borderRadius: '20px',
                px: { xs: 1, md: 2 },
                py: { xs: 0.25, md: 0.5 },
                ...glassEffect,
                fontSize: { xs: '0.75rem', md: '0.9rem' },
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.05),
                }
              }}
            >
              {post.commentsCount}
            </Button>

            <Button
              startIcon={<Share sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
              size="small"
              sx={{
                minWidth: 'auto',
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                fontWeight: 600,
                borderRadius: '20px',
                px: { xs: 1, md: 2 },
                py: { xs: 0.25, md: 0.5 },
                fontSize: { xs: '0.75rem', md: '0.85rem' },
                ...glassEffect,
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.05),
                }
              }}
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/post/${post.id}`);
              }}
            >
              Share
            </Button>

            <IconButton
              size="small"
              onClick={(e) => handlePostMenuOpen(e, post.id)}
              sx={{ 
                ml: 'auto', 
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                '&:hover': { 
                  bgcolor: alpha(goldColor, 0.1),
                } 
              }}
            >
              <MoreVert fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      </Box>
      {index < posts.length - 1 && (
        <Divider sx={{ 
          borderColor: alpha(goldColor, 0.1),
          mx: { xs: 2, md: 3 }
        }} />
      )}
    </React.Fragment>
  );
};

export default PostCard;